import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FiEdit2 } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import { aboutApi } from '../../api/about';
import { useUserStore } from '../../stores/userStore';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import ContentViewer from '../../components/common/ContentViewer';
import ProgressBar from '../../components/common/ProgressBar';
import ReactQuillEditor from '../../components/Editor/ReactQuillEditor';
import BasicButton from '../../components/button/BasicButton';
import CancelConfirmDialog from '../../components/dialog/CancelConfirmDialog';
import DraftGuard from '../contact/ContactDraftGuard';
import { translate } from '../contact/model';
import { AccountBoundary, useRequestScope, useSettings } from '../settings/shared';
import { documentTypes } from './routes';
import { SITE_URL } from '../../utils/env';

const titles = { about: 'About', terms: '이용약관', privacy: '개인정보처리방침' };
const hasText = (value) =>
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

// Admin authoring is a web-only tool. Keep the native-style public reader separate.
export default function AdminDocument({ type }) {
  const location = useLocation();
  const Component = useMemo(
    () =>
      function BoundDocument(props) {
        return <DocumentEditor {...props} type={type} />;
      },
    [type],
  );
  return <AccountBoundary key={`${type}:${location.key}`} component={Component} />;
}

function DocumentEditor({ type, user, validateSession }) {
  const isAdmin = useUserStore((s) => s.isAdmin);
  const { locale: uiLocale } = useSettings();
  const request = useRequestScope(user);
  const [locale, setLocale] = useState('ko');
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const lock = useRef(false),
    editButton = useRef(null),
    focusFrame = useRef(null);
  const guard = useRef({ dirty: false, busy: false, released: false });
  const original = document?.fallback ? '' : document?.content || '';
  guard.current.dirty = editing && draft !== original;
  guard.current.busy = saving;
  const apiType = documentTypes[type];

  useEffect(() => {
    if (!isAdmin) return;
    const r = request();
    setLoading(true);
    setLoadError(false);
    setDocument(null);
    aboutApi
      .getContent(apiType, locale, r.options)
      .then(({ data }) => {
        if (r.current())
          setDocument({ content: data.content || '', fallback: Boolean(data.fallback) });
      })
      .catch(() => {
        if (r.current()) setLoadError(true);
      })
      .finally(() => {
        if (r.mounted() && !r.options.signal.aborted) {
          setLoading(false);
          if (!r.current()) setLoadError(true);
        }
        r.finish();
      });
    return r.cancel;
    // request is render-local; effect identity follows document selection and account boundary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiType, locale, reload, isAdmin]);
  useEffect(() => () => cancelAnimationFrame(focusFrame.current), []);

  const close = () => {
    setEditing(false);
    setDraft('');
    setSaveError(false);
    setCancelOpen(false);
    guard.current.dirty = false;
    focusFrame.current = requestAnimationFrame(() =>
      editButton.current?.focus({ preventScroll: true }),
    );
  };
  const begin = () => {
    if (!validateSession() || !isAdmin || loading || loadError || !document) return;
    guard.current.released = false;
    setDraft(original);
    setSaveError(false);
    setEditing(true);
  };
  const save = async () => {
    if (
      lock.current ||
      !validateSession() ||
      !isAdmin ||
      !document ||
      (type !== 'about' && !hasText(draft))
    )
      return;
    lock.current = true;
    guard.current.busy = true;
    setSaving(true);
    setSaveError(false);
    const r = request();
    try {
      const { data } = await aboutApi.updateContent(draft, apiType, locale, r.options);
      if (!r.current()) return;
      setDocument({ content: data.content, fallback: false });
      close();
    } catch {
      if (r.current()) setSaveError(true);
    } finally {
      if (r.mounted()) {
        lock.current = false;
        guard.current.busy = false;
        setSaving(false);
        if (!r.current()) validateSession();
      }
      r.finish();
    }
  };
  if (!isAdmin) return null;
  return (
    <PageLayout>
      {type === 'about' && (
        <>
          <SEO
            title="About"
            description={document?.content?.replace(/<[^>]*>?/gm, '').substring(0, 160)}
            url={`${SITE_URL}/about`}
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                  { '@type': 'ListItem', position: 2, name: 'About' },
                ],
              })}
            </script>
          </Helmet>
        </>
      )}
      <DraftGuard state={guard} t={(key, values) => translate(uiLocale, key, values)} />
      <Stack spacing="30px" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {editing ? (
          <>
            <Typography variant="body2" color="text.secondary">
              편집 언어: {locale === 'en' ? 'English' : '한국어'}
            </Typography>
            {locale === 'en' && document?.fallback && (
              <Alert severity="info">
                영문 {titles[type]}이 아직 없습니다. 아래 편집기는 빈 영문 문서로 시작하며 한국어
                원문은 변경하지 않습니다.
              </Alert>
            )}
            <Box
              inert={saving ? '' : undefined}
              aria-busy={saving}
              sx={{ minWidth: 0, '& > div': { minWidth: '0 !important' } }}
            >
              <ReactQuillEditor
                value={draft}
                onChange={(value) => {
                  if (!lock.current) setDraft(value);
                }}
              />
            </Box>
            {saveError && (
              <Alert severity="error">
                문서를 저장하지 못했습니다. 작성한 내용을 유지했습니다. 다시 시도해 주세요.
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                mt: { xs: '30px', lg: '50px' },
              }}
            >
              <BasicButton
                fullWidth
                variant="outlined"
                disabled={saving}
                onClick={() => {
                  if (!validateSession() || lock.current) return;
                  if (guard.current.dirty) setCancelOpen(true);
                  else close();
                }}
                sx={{ width: { md: '110px' }, height: '48px' }}
              >
                취소
              </BasicButton>
              <BasicButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={saving || (type !== 'about' && !hasText(draft))}
                onClick={save}
                sx={{ width: { md: '110px' }, height: '48px' }}
              >
                등록
              </BasicButton>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: type === 'about' ? 'flex-end' : 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                pb: { lg: type === 'about' ? 0 : 2 },
              }}
            >
              {type !== 'about' && (
                <Typography fontSize={{ lg: 28, md: 22, xs: 20 }} fontWeight="bold">
                  {titles[type]}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {type !== 'about' && (
                  <ToggleButtonGroup
                    value={locale}
                    exclusive
                    size="small"
                    aria-label="문서 언어"
                    onChange={(_e, value) => {
                      if (value && value !== locale && validateSession()) setLocale(value);
                    }}
                  >
                    <ToggleButton value="ko">한국어</ToggleButton>
                    <ToggleButton value="en">English</ToggleButton>
                  </ToggleButtonGroup>
                )}
                <IconButton
                  ref={editButton}
                  aria-label="toggle edit"
                  disabled={loading || loadError || !document}
                  onClick={begin}
                  sx={{ width: 44, height: 44 }}
                >
                  <FiEdit2 size={20} />
                </IconButton>
              </Box>
            </Box>
            {loadError && (
              <Alert
                severity="error"
                action={
                  <Button
                    onClick={() => {
                      if (validateSession()) setReload((v) => v + 1);
                    }}
                  >
                    다시 시도
                  </Button>
                }
              >
                문서를 불러오지 못했습니다.
              </Alert>
            )}
            <ProgressBar isLoading={loading}>
              {!loadError && document && (
                <>
                  {locale === 'en' && document.fallback && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      공식 영문본이 없어 한국어 원문을 미리보기로 표시하고 있습니다. 편집을 누르면
                      빈 영문 문서에서 시작합니다.
                    </Alert>
                  )}
                  <Box
                    lang={document.fallback ? 'ko' : locale}
                    sx={{
                      minWidth: 0,
                      ...(type !== 'about'
                        ? { userSelect: 'none', WebkitUserSelect: 'none', cursor: 'default' }
                        : {}),
                    }}
                  >
                    <ContentViewer text={document.content} />
                  </Box>
                </>
              )}
            </ProgressBar>
          </>
        )}
      </Stack>
      <CancelConfirmDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={() => {
          if (!lock.current && validateSession()) close();
        }}
      />
    </PageLayout>
  );
}
